import { useCallback } from 'react';
import { useProjectStore } from '../store/useProjectStore';
import { useNavigation } from './useNavigation';
import { Project, ProjectType, TranslationFile } from '../types';
import { DEFAULT_SYSTEM_PROMPT, DEFAULT_RULES } from '../store/useConfigStore';
import { exportProjectToZip, importProjectFromZip } from '../utils/projectStorage';
import { matchFiles } from '../utils/fileMatcher';
import { parseFile } from '../utils/fileParser';
import { fileSystemPersistence } from '../services/shared/fileSystemPersistence';
import { useFileSystemStore } from '../store/useFileSystemStore';
import { isCommercial } from '../config/appMode';
import { getIndustryPrompt } from '../utils/industryPrompts';

export const useProjectActions = () => {
    const {
        projects,
        files,
        addProject,
        updateProject,
        deleteProject,
        selectProject,
        addFiles,
        selectedProjectId
    } = useProjectStore();

    const { setCurrentView, setIsCreateModalOpen, setReturnToFileId, setProjectDetailTab } = useNavigation();

    const handleCreateProject = useCallback((name: string, type: ProjectType) => {
        // US-17-06: Commercial mode — auto-apply user's preferred industry preset + langs
        let systemPrompt = DEFAULT_SYSTEM_PROMPT;
        let sourceLang: string | undefined;
        let targetLang: string | undefined;
        let industry: string | undefined;

        if (isCommercial && typeof window !== 'undefined') {
            const savedIndustry = localStorage.getItem('tour_industry');
            const savedSource = localStorage.getItem('tour_source_lang');
            const savedTarget = localStorage.getItem('tour_target_lang');
            if (savedIndustry) {
                const presetPrompt = getIndustryPrompt(savedIndustry);
                if (presetPrompt) {
                    systemPrompt = presetPrompt;
                    industry = savedIndustry;
                }
            }
            if (savedSource) sourceLang = savedSource;
            if (savedTarget) targetLang = savedTarget;
        }

        const newProject: Project = {
            id: crypto.randomUUID(),
            type,
            name: name || 'New Project',
            systemPrompt,
            synonymGroups: [],
            sourceLang,
            targetLang,
            industry,
            createdAt: new Date()
        };
        addProject(newProject);
        selectProject(newProject.id);
        setCurrentView('project-detail');
        setIsCreateModalOpen(false);
    }, [addProject, selectProject, setCurrentView, setIsCreateModalOpen]);

    const handleUpdateProject = useCallback((updatedProject: Project) => {
        updateProject(updatedProject);
    }, [updateProject]);

    const handleImportProject = useCallback(async (file: File) => {
        try {
            const { project, files: importedFiles } = await importProjectFromZip(file);
            addProject(project);
            addFiles(importedFiles);
            selectProject(project.id);
            setCurrentView('project-detail');
        } catch (err) {
            console.error('Import failed:', err);
        }
    }, [addProject, addFiles, selectProject, setCurrentView]);

    const handleExportProject = useCallback(async (projectParam?: Project) => {
        const targetProject = projectParam || projects.find(p => p.id === selectedProjectId);
        if (targetProject) {
            // US-19-16: ensure all project files have full content (sourceText, originalFile, aiOutput)
            // loaded before zip export. Export reads heavy fields directly.
            const { ensureFileContentLoaded } = await import('./useFileSystem');
            const projectFiles = files.filter(f => f.projectId === targetProject.id);
            await Promise.all(projectFiles.map(f => ensureFileContentLoaded(f.id)));
            // Re-read after loads
            const freshFiles = projectFiles.map(f => useProjectStore.getState().files.find(x => x.id === f.id) || f);
            await exportProjectToZip(targetProject, freshFiles);
        }
    }, [projects, files, selectedProjectId]);

    const handleOpenFolder = useCallback(async (folderFiles: File[]) => {
        if (folderFiles.length === 0) return;

        const firstFile = folderFiles[0];
        const pathParts = firstFile.webkitRelativePath?.split('/') || [firstFile.name];
        const folderName = pathParts.length > 1 ? pathParts[0] : 'Chunks Project';

        const projectId = crypto.randomUUID();
        const newProject: Project = {
            id: projectId,
            name: folderName,
            type: 'train',
            createdAt: new Date(),
            humanBenchmark: '',
            synonymGroups: [],
            systemPrompt: DEFAULT_SYSTEM_PROMPT
        };

        const pairs = matchFiles(folderFiles);
        const newFiles: TranslationFile[] = [];

        for (const pair of pairs) {
            if (!pair.source) continue;
            const { text: sourceText, originalFormat: srcFormat, arrayBuffer: srcBuffer, docxParagraphMap: srcParaMap } = await parseFile(pair.source.file);
            const benchmarkText = pair.benchmark ? (await parseFile(pair.benchmark.file)).text : '';

            newFiles.push({
                id: crypto.randomUUID(),
                projectId,
                fileName: pair.source.file.name,
                sourceText,
                humanBenchmark: benchmarkText || undefined,
                translationStatus: 'pending',
                auditStatus: 'pending',
                currentStep: benchmarkText ? 2 : 1,
                createdAt: new Date(),
                originalFile: srcBuffer,
                originalFormat: srcFormat,
                docxParagraphMap: srcParaMap,
            });
        }

        addProject(newProject);
        addFiles(newFiles);
        selectProject(projectId);
        setCurrentView('project-detail');
    }, [addProject, addFiles, selectProject, setCurrentView]);

    const handleDeleteProject = useCallback(async (projectId: string) => {
        if (window.confirm('Ban co chac muon xoa project nay? Tat ca file lien quan se bi mat.')) {
            console.log('[DeleteProject] START: Deleting project', projectId);

            // 1. Delete from disk FIRST (before store removal triggers auto-save)
            try {
                const project = projects.find(p => p.id === projectId);
                console.log('[DeleteProject] Found project:', project?.name);
                console.log('[DeleteProject] fileSystemPersistence.handle exists:', !!(fileSystemPersistence as any).handle);

                // Ensure handle is set from the current rootHandle
                const rootHandle = useFileSystemStore.getState().rootHandle;
                if (rootHandle) {
                    (fileSystemPersistence as any).handle = rootHandle;
                    console.log('[DeleteProject] Set handle from rootHandle:', rootHandle.name);
                }

                await fileSystemPersistence.deleteProjectFolder(projectId, project?.name);
                console.log('[DeleteProject] Disk deletion completed');
            } catch (err) {
                console.error('[DeleteProject] Disk cleanup error:', err);
            }

            // 2. Then delete from store
            console.log('[DeleteProject] Removing from store...');
            deleteProject(projectId);
            if (selectedProjectId === projectId) {
                selectProject(null);
                setCurrentView('projects');
            }
            console.log('[DeleteProject] DONE. Store projects count:', useProjectStore.getState().projects.length);
        }
    }, [deleteProject, selectedProjectId, selectProject, setCurrentView, projects]);


    const handleRenameProject = useCallback((projectId: string, newName: string) => {
        const project = projects.find(p => p.id === projectId);
        if (project) {
            updateProject({ ...project, name: newName });
        }
    }, [projects, updateProject]);

    return {
        handleCreateProject,
        handleUpdateProject,
        handleImportProject,
        handleExportProject,
        handleOpenFolder,
        handleDeleteProject,
        handleRenameProject
    };
};
